package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.ast.FunDecl
import top.wcpe.allincore.dsl.compiler.ast.StmtAst
import top.wcpe.allincore.dsl.compiler.ast.TypeAst
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.lex.TokenType
import top.wcpe.allincore.dsl.compiler.semantic.DefaultValueChecker

/**
 * `fun NAME(params) { body }` 声明解析器（C4）。
 *
 * body 解析语义与 lambda body 对齐（[ActionBlockParser.parseLambdaBody]）：
 * - 参数作为局部变量声明进独立 block 作用域
 * - body 可包含 return，视为 fun 自身的返回值
 * - body 末尾允许裸表达式作为返回值
 *
 * 既被 [ScreenParser] 用于解析 screen 级 fun，也被 [ActionBlockParser] 用于解析
 * 块内的局部 fun——两种位置共用相同的语法规则。
 */
internal class FunDeclParser(private val p: Parser) {

    /**
     * 解析 screen 级 fun（与 state/action/animation 平级）。
     *
     * @param isSuspend 调用方已识别并消费过 `suspend` 软关键字前缀时传入 true，
     *                  代表用户显式声明 `suspend fun`。
     */
    fun parseScreenLevel(isSuspend: Boolean = false): FunDecl = parseInternal(isSuspend)

    /**
     * 解析局部块内 fun。返回 [StmtAst.FunDeclStmt]；调用方负责将 name 注册进 declareLocal。
     *
     * @param isSuspend 调用方已识别并消费过 `suspend` 软关键字前缀时传入 true。
     */
    fun parseLocal(isSuspend: Boolean = false): StmtAst.FunDeclStmt {
        val decl = parseInternal(isSuspend)
        return StmtAst.FunDeclStmt(decl, decl.position)
    }

    private fun parseInternal(isSuspend: Boolean): FunDecl {
        val funTok = p.expect(TokenType.KW_FUN)
        val nameTok = p.expect(TokenType.IDENTIFIER, "fun 后缺少函数名")
        p.expect(TokenType.LPAREN, "fun 名后缺少 '('")
        val (params, defaults, paramTypes) = parseParams()
        p.expect(TokenType.RPAREN, "fun 参数列表缺少 ')'")
        // M5-c：可选返回类型标注 `: Type`，位于 `)` 之后、`{` 之前
        val returnType: TypeAst? = if (p.check(TokenType.COLON)) {
            p.advance()
            TypeParser.parse(p)
        } else null
        p.expect(TokenType.LBRACE, "fun 头部后缺少 '{'")
        p.skipNewlines()

        // I3：默认值不得引用后续形参，参数解析完成后统一扫描
        DefaultValueChecker.checkAndFail(p, params, defaults)

        // 复用 lambda body 解析：push scope + 声明 params + enterReturnable，
        // 解析到 `}` 之前，但不消费 `}`；语义允许末尾裸表达式作为返回值
        val body = ActionBlockParser(p).parseLambdaBody(params, allowBareIdent = false)
        val close = p.expect(TokenType.RBRACE, "fun 体缺少 '}'")
        return FunDecl(
            name = nameTok.lexeme,
            params = params,
            body = body,
            position = p.completePosition(funTok.position, close),
            defaults = defaults,
            paramTypes = paramTypes,
            returnType = returnType,
            isSuspend = isSuspend,
        )
    }

    /**
     * 解析形参列表，支持默认实参与类型标注：
     * - `name`
     * - `name: Type`
     * - `name = defaultExpr`
     * - `name: Type = defaultExpr`
     *
     * 校验：必填形参不能出现在有默认值的形参之后（E616）。
     *
     * 返回三元组：(形参名列表, 默认值列表, 形参类型列表)。三者长度均等于形参个数；
     * 未填写的默认值 / 类型对应位置为 null。
     */
    private fun parseParams(): Triple<List<String>, List<ExprAst?>, List<TypeAst?>> {
        if (p.check(TokenType.RPAREN)) return Triple(emptyList(), emptyList(), emptyList())
        val names = mutableListOf<String>()
        val defaults = mutableListOf<ExprAst?>()
        val types = mutableListOf<TypeAst?>()
        var seenDefault = false

        parseOneParam(names, defaults, types, seenDefault).also { seenDefault = it }
        while (p.check(TokenType.COMMA)) {
            p.advance()
            p.skipNewlines()
            parseOneParam(names, defaults, types, seenDefault).also { seenDefault = it }
        }
        return Triple(names, defaults, types)
    }

    /** 解析单个形参，返回是否已见过默认值。 */
    private fun parseOneParam(
        names: MutableList<String>,
        defaults: MutableList<ExprAst?>,
        types: MutableList<TypeAst?>,
        seenDefault: Boolean,
    ): Boolean {
        val nameTok = p.expect(TokenType.IDENTIFIER, "fun 参数名缺失")
        names += nameTok.lexeme
        // M5-c：可选类型标注 `: Type`
        val paramType: TypeAst? = if (p.check(TokenType.COLON)) {
            p.advance()
            TypeParser.parse(p)
        } else null
        types += paramType
        return if (p.check(TokenType.EQUALS)) {
            p.advance()
            p.skipNewlines()
            // 默认值表达式可引用前序形参；此时前序形参尚未进入 local scope，
            // 开启 allowBareIdent=true 让裸标识符升格为 IdentRef，供 DefaultValueChecker 后续扫描。
            defaults += ExpressionParser(p, allowBareIdent = true).parse()
            true
        } else {
            // M5-b E616：必填形参不能出现在有默认值的形参之后
            if (seenDefault) {
                p.fail(DslError.E616_REQUIRED_AFTER_DEFAULT, nameTok.position, nameTok.lexeme)
            }
            defaults += null
            seenDefault
        }
    }
}
